import React, { FC, useState } from 'react';
import { Consumer } from '../consumer.types';
import cls from './ConsumerInfo.module.scss';
import ConsumerIcon from '../../widget/Icon';
import ConsumerActionButtons from '../ConsumerActionButtons';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';
import { useGetConsumerMagicLinkMutation } from '../consumer.service';
import { toast } from 'react-toastify';
import { formatConsumerType } from '../consumer.utils';
import { copyToClipboard } from '../../../utils/copyToClipboard';
type ConsumerInfoProps = {
  consumer: Consumer;
};
const getBaseUrl = () => {
  return process.env.FRONTEND_URL || window.location.origin;
};
const ConsumerInfo: FC<ConsumerInfoProps> = ({ consumer }) => {
  const dispatch = useAppDispatch();
  const [getMagicLink] = useGetConsumerMagicLinkMutation();
  const [isFetchingMagicLink, setIsFetchingMagicLink] = useState(false);
  const handleCopyMagicLink = async () => {
    if (!consumer.availableSupportMagicLink || isFetchingMagicLink) return;

    try {
      setIsFetchingMagicLink(true);
      const response = await getMagicLink({ consumerId: consumer.id }).unwrap();

      if (!response?.magicLinkCode) {
        throw new Error('Failed to get code');
      }

      const fullUrl = `${getBaseUrl()}/?code=${response.magicLinkCode}`;
      const copied = await copyToClipboard(fullUrl);

      if (copied) {
        toast.success('Code copied to clipboard', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error(`Failed to copy, use the link: ${fullUrl}`, {
          position: 'top-right',
          autoClose: false,
        });
      }
    } catch {
      toast.error('Failed to get access code', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsFetchingMagicLink(false);
    }
  };
  const openDisableModal = () =>
    dispatch(
      openModal({
        type: 'disableConsumer2FA',

        extra: {
          id: consumer?.id,
          title: 'Disable two-factor authentication?',
          supportText: 'Two-factor authentication for account ',
          textInformation: consumer?.email,
          subSupportText: ' will be disabled.',
          actionButtonText: 'Disable',
        },
      }),
    );
  return (
    <div className={cls.ConsumerInfo} data-testid="consumer-info">
      <div className={cls.Consumer}>
        <ConsumerIcon email={consumer?.email ?? ''} name={consumer?.name} />
        <div className={cls.Info}>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Name</span>
            <span className={cls.FieldValue}>{consumer?.name ?? 'Not specified'}</span>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Phone number</span>
            <span className={cls.FieldValue}>
              {consumer.phone && consumer?.phone.trim().length > 0
                ? consumer.phone
                : 'Not specified'}
            </span>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Email</span>
            <a href={`mailto:${consumer?.email}`} className={`${cls.FieldValue} ${cls.FieldLink}`}>
              {consumer?.email}
            </a>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Type</span>
            <span className={cls.FieldValue}>{formatConsumerType(consumer.consumerType)}</span>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Two-factor authentication</span>
            <div className={cls.Using2FAContainer}>
              <span className={cls.FieldValue}>{consumer.using2FA ? 'Yes' : 'No'}</span>
              {consumer.using2FA && (
                <button className={cls.Button} onClick={openDisableModal}>
                  Disable
                </button>
              )}
            </div>
            <div className={cls.Property}>
              <span className={cls.FieldName}>Support service connection</span>
              <span
                className={`${cls.FieldValue} ${
                  consumer.availableSupportMagicLink ? cls.Clickable : ''
                }`}
                onClick={consumer.availableSupportMagicLink ? handleCopyMagicLink : undefined}
                style={{
                  cursor: consumer.availableSupportMagicLink ? 'pointer' : 'default',
                  // blue only for the available state, everything else keeps its own colour
                  color: consumer.availableSupportMagicLink ? 'rgba(11, 110, 253, 1)' : undefined,
                }}
              >
                {consumer.availableSupportMagicLink
                  ? isFetchingMagicLink
                    ? 'Loading...'
                    : 'Available'
                  : 'Not available'}
              </span>
            </div>
          </div>
        </div>
      </div>
      <ConsumerActionButtons consumer={consumer} />
    </div>
  );
};

export default ConsumerInfo;
